import { Component, computed, inject, input, signal } from '@angular/core';
import { catchError, finalize, throwError } from 'rxjs';
import { CreateSharedLinkRequestDto, CreateSharedLinkResponse } from '../../../shared/models/share-tests.model';
import { BrowserUtils } from '../../../shared/services/browser-utils';
import { ExportService } from '../../../shared/services/export.service';
import { DialogRef } from '../../../shared/services/modal.service';
import { NotificationService } from '../../../shared/services/notification.service';

@Component({
  standalone: false,
  selector: 'app-share-test-results-modal',
  templateUrl: './share-test-results-modal.component.html',
  styleUrls: ['./share-test-results-modal.component.scss'],
})
export class ShareTestResultsModalComponent {
  readonly dialogRef = input<DialogRef>();
  readonly shareTestResultsInfo = input<CreateSharedLinkRequestDto>();

  readonly exportService = inject(ExportService);
  readonly notificationService = inject(NotificationService);

  readonly loading = signal<boolean>(false);
  readonly generatedLinkInfo = signal<CreateSharedLinkResponse | null>(null);

  readonly shareTestsCount = computed(() => this.shareTestResultsInfo().testIds.length);
  readonly shareTestsIdsString = computed(() => this.shareTestResultsInfo().testIds.join(' '));
  readonly generatedUrlLinkFull = computed(() => {
    if (this.generatedLinkInfo()) {
      const urlPrefix = BrowserUtils.getHostUrl();

      return `${urlPrefix}/#/public/shared-tests/${this.generatedLinkInfo().token}`;
    }

    return '';
  });

  onCancelClick(): void {
    this.dialogRef().close(false);
  }

  onGenerateLinkClick(): void {
    this.loading.set(true);

    this.exportService
      .createSharedLinkForTests(this.shareTestResultsInfo())
      .pipe(
        catchError(e => {
          this.notificationService.error({
            title: 'Shared Link',
            message: 'An error occurred while creating shared link for tests.',
            requestMessage: e.statusText,
            requestCode: e.status,
            ts: e.timestamp ? e.timestamp : null,
            serviceName: 'Export Service',
          });
          return throwError(() => e);
        }),
        finalize(() => this.loading.set(false))
      )
      .subscribe(res => this.generatedLinkInfo.set(res));
  }

  onCopyLinkClick() {
    BrowserUtils.copyToClipboard(this.generatedUrlLinkFull());

    this.notificationService.success('Shared link was copied to clipboard', 'Shared Link');
  }
}
